import { useCallback, useEffect, useState } from 'react';
import {
  Stack,
  Group,
  Text,
  Paper,
  Collapse,
  Box,
  Avatar,
  Badge,
  ActionIcon,
  Select,
  Button,
  Alert,
  Loader,
  Center,
  Tooltip,
} from '@mantine/core';
import { ChevronDown, ChevronRight, Edit2 } from 'react-feather';
import {
  sessionNumberConfigsApi,
  ONE_ON_ONE_TYPE_LABELS,
  type BySessionOneOnOneEntry,
  type BySessionOneOnOneParticipantRow,
  type OneOnOneType,
} from '../../../../services/api/sessionNumberConfigs';
import { isCheckinOneOnOneType } from '../../../../types';
import { formatDateTime } from '../../../../utils/formatters';

interface OneOnOneBySessionViewProps {
  cohortId: string;
  coachUserId?: string;
  readOnly?: boolean;
  /** MED-1350: show the check-in entries only (a partner coach). */
  checkinsOnly?: boolean;
  onSessionClick?: (sessionId: string) => void;
  refreshKey?: number;
}

const TYPE_OPTIONS: Array<{ value: OneOnOneType; label: string }> = (
  Object.entries(ONE_ON_ONE_TYPE_LABELS) as Array<[OneOnOneType, string]>
).map(([value, label]) => ({ value, label }));

function StatusPill({ status, absent }: { status: string; absent?: boolean }) {
  const { label, color } =
    status === 'completed' && absent
      ? { label: 'Absent', color: 'red' }
      : status === 'completed'
        ? { label: 'Complete', color: 'medleyGreen' }
        : status === 'scheduled'
          ? { label: 'Scheduled', color: 'blue' }
          : status === 'cancelled'
            ? { label: 'Cancelled', color: 'gray' }
            : { label: 'Not Scheduled', color: 'gray' };
  return (
    <Badge
      size="xs"
      color={color}
      variant="light"
      tt="uppercase"
      style={{ letterSpacing: '0.3px' }}
    >
      {label}
    </Badge>
  );
}

export function OneOnOneBySessionView({
  cohortId,
  coachUserId,
  readOnly = false,
  checkinsOnly = false,
  onSessionClick,
  refreshKey,
}: OneOnOneBySessionViewProps) {
  const [entries, setEntries] = useState<BySessionOneOnOneEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // MED-1350: keyed by entry_key, because a check-in entry has no number.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editBuffer, setEditBuffer] = useState<OneOnOneType | null>(null);
  const [saving, setSaving] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await sessionNumberConfigsApi.getBySessionNumber(
        cohortId,
        'one_on_one',
        coachUserId,
      );
      if (res.modality !== 'one_on_one') {
        setEntries([]);
      } else {
        setEntries(
          checkinsOnly
            ? res.session_numbers.filter((e) => isCheckinOneOnOneType(e.session_type))
            : res.session_numbers,
        );
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load 1:1 by-session view');
    } finally {
      setLoading(false);
    }
  }, [cohortId, coachUserId, checkinsOnly]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const toggleExpanded = useCallback((key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const beginEdit = useCallback((entry: BySessionOneOnOneEntry) => {
    // The API refuses a type change on a check-in, so a check-in never edits.
    if (isCheckinOneOnOneType(entry.session_type)) return;
    setEditingKey(entry.entry_key);
    setEditBuffer(entry.session_type);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingKey(null);
    setEditBuffer(null);
  }, []);

  const saveEdit = useCallback(
    async (entry: BySessionOneOnOneEntry) => {
      if (!editBuffer || entry.session_number == null) return;
      setSaving(entry.entry_key);
      try {
        await sessionNumberConfigsApi.propagateOneOnOneType(
          cohortId,
          entry.session_number,
          editBuffer,
        );
        await load();
        setEditingKey(null);
        setEditBuffer(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to save session type');
      } finally {
        setSaving(null);
      }
    },
    [cohortId, editBuffer, load],
  );

  if (loading) {
    return (
      <Center p="xl">
        <Loader size="sm" />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert color="red" variant="light" title="Could not load 1:1 sessions">
        {error}
      </Alert>
    );
  }

  if (entries.length === 0) {
    return (
      <Paper p="lg" radius="md" withBorder>
        <Text size="sm" c="dimmed" ta="center">
          No 1:1 coaching sessions yet.
        </Text>
      </Paper>
    );
  }

  return (
    <Stack gap="sm">
      {entries.map((entry) => {
        const isCheckin = isCheckinOneOnOneType(entry.session_type);
        const isOpen = expanded.has(entry.entry_key);
        const isEditing = !isCheckin && editingKey === entry.entry_key;
        const isSaving = saving === entry.entry_key;
        const entryLabel = entry.label ?? `Session ${entry.session_number}`;
        const scheduledCount = entry.participants_scheduled; // includes completed per service
        const alreadyScheduledInThisNumber = entry.participants.filter(
          (p) => p.status === 'scheduled' || p.status === 'completed',
        ).length;
        const typeLabel =
          entry.session_type && !isCheckinOneOnOneType(entry.session_type)
            ? ONE_ON_ONE_TYPE_LABELS[entry.session_type]
            : 'Set session type';

        return (
          <Paper
            key={entry.entry_key}
            radius="md"
            style={{ border: '1px solid var(--mantine-color-gray-2)' }}
          >
            <Box
              role="button"
              tabIndex={0}
              onClick={() => toggleExpanded(entry.entry_key)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ')
                  toggleExpanded(entry.entry_key);
              }}
              style={{ cursor: 'pointer' }}
            >
              {/* MED-1350: fixed header height, 68 px for a session, 56 px for a check-in */}
              <Group
                justify="space-between"
                px="md"
                wrap="nowrap"
                style={{
                  minHeight: isCheckin ? 56 : 68,
                  borderBottom: isOpen ? '1px solid var(--mantine-color-gray-2)' : 'none',
                }}
              >
                <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                  {/* MED-1350: flex-shrink 0 keeps the chevron at full size on a phone */}
                  {isOpen ? (
                    <ChevronDown size={16} color="var(--mantine-color-dimmed)" style={{ flexShrink: 0 }} />
                  ) : (
                    <ChevronRight size={16} color="var(--mantine-color-dimmed)" style={{ flexShrink: 0 }} />
                  )}
                  <Text fw={600} size="sm" style={{ whiteSpace: 'nowrap' }}>
                    {entryLabel}
                  </Text>
                  {/* MED-1350: the dot shows only when something follows it */}
                  {(!isCheckin || entry.target_week != null) && (
                    <Text size="sm" c="dimmed">
                      ·
                    </Text>
                  )}
                  {isCheckin ? (
                    entry.target_week != null && (
                      <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                        Week {entry.target_week}
                      </Text>
                    )
                  ) : isEditing ? (
                    <Group
                      gap="xs"
                      wrap="nowrap"
                      style={{ flex: 1 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Select
                        data={TYPE_OPTIONS}
                        value={editBuffer}
                        onChange={(v) => setEditBuffer(v as OneOnOneType | null)}
                        size="sm"
                        placeholder="Select session type"
                        style={{ minWidth: 260 }}
                        disabled={isSaving}
                      />
                      <Button
                        size="xs"
                        color="medleyGreen"
                        onClick={() => saveEdit(entry)}
                        loading={isSaving}
                        disabled={!editBuffer}
                      >
                        Save
                      </Button>
                      <Button
                        size="xs"
                        variant="subtle"
                        color="gray"
                        onClick={cancelEdit}
                        disabled={isSaving}
                      >
                        Cancel
                      </Button>
                    </Group>
                  ) : (
                    <Group gap="xs" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                      <Text
                        size="sm"
                        fw={500}
                        c={entry.session_type ? undefined : 'dimmed'}
                        truncate
                      >
                        {typeLabel}
                      </Text>
                      {!readOnly && (
                        <Tooltip label="Change session type">
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            color="gray"
                            onClick={(e) => {
                              e.stopPropagation();
                              beginEdit(entry);
                            }}
                            aria-label="Edit session type"
                          >
                            <Edit2 size={12} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Group>
                  )}
                </Group>

                <Group gap="lg" wrap="nowrap" style={{ flexShrink: 0 }}>
                  <Stack gap={0} align="flex-end">
                    <Text size="xs" c="dimmed">
                      Scheduled
                    </Text>
                    <Text size="xs" fw={500}>
                      {scheduledCount}/{entry.participants_total}
                    </Text>
                  </Stack>
                  <Stack gap={0} align="flex-end">
                    <Text size="xs" c="dimmed">
                      Completed
                    </Text>
                    <Text size="xs" fw={500}>
                      {entry.participants_completed}/{entry.participants_total}
                    </Text>
                  </Stack>
                </Group>
              </Group>

              {isEditing && (
                <Box
                  px="md"
                  pb="sm"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* AC28: confirmation message with count of sessions that WILL update */}
                  <Text size="xs" c="dimmed">
                    This will update {entry.participants_total - alreadyScheduledInThisNumber}{' '}
                    unscheduled session
                    {entry.participants_total - alreadyScheduledInThisNumber === 1 ? '' : 's'}{' '}
                    for {entryLabel}.
                  </Text>
                  {/* AC29: note for already-scheduled sessions that will NOT update */}
                  {alreadyScheduledInThisNumber > 0 && (
                    <Text size="xs" c="dimmed">
                      {alreadyScheduledInThisNumber} scheduled session
                      {alreadyScheduledInThisNumber === 1 ? '' : 's'} will not be updated.
                    </Text>
                  )}
                </Box>
              )}
            </Box>

            <Collapse in={isOpen}>
              <Stack gap={0}>
                {entry.participants.map((p) => (
                  <ParticipantRow
                    key={p.session_id}
                    row={p}
                    showCoach={!readOnly}
                    onClick={p.status !== 'unscheduled' ? () => onSessionClick?.(p.session_id) : undefined}
                  />
                ))}
              </Stack>
            </Collapse>
          </Paper>
        );
      })}
    </Stack>
  );
}

interface ParticipantRowProps {
  row: BySessionOneOnOneParticipantRow;
  showCoach: boolean;
  onClick?: () => void;
}

function ParticipantRow({ row, showCoach, onClick }: ParticipantRowProps) {
  const displayName =
    [row.first_name, row.last_name].filter(Boolean).join(' ') || row.email || 'Unknown';
  const scheduled = row.scheduled_start
    ? formatDateTime(row.scheduled_start, row.timezone ?? undefined)
    : '—';
  return (
    <Group
      justify="space-between"
      p="sm"
      wrap="nowrap"
      style={{
        cursor: onClick ? 'pointer' : 'default',
        borderTop: '1px solid var(--mantine-color-gray-1)',
      }}
      onClick={onClick}
    >
      <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
        <Avatar size="sm" radius="xl" src={row.avatar_url ?? undefined}>
          {displayName.charAt(0).toUpperCase()}
        </Avatar>
        <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
          <Text size="sm" fw={500} truncate>
            {displayName}
          </Text>
          {row.email && (
            <Text size="xs" c="dimmed" truncate>
              {row.email}
            </Text>
          )}
        </Stack>
      </Group>
      <Group gap="lg" wrap="nowrap" style={{ flexShrink: 0 }}>
        {showCoach && (
          <Stack gap={0} align="flex-end" miw={120}>
            <Text size="xs" c="dimmed">
              Coach
            </Text>
            <Text size="xs" fw={500} truncate>
              {row.coach_name ?? '—'}
            </Text>
          </Stack>
        )}
        <Stack gap={0} align="flex-end" miw={160}>
          <Text size="xs" c="dimmed">
            When
          </Text>
          <Text size="xs" fw={500}>
            {scheduled}
          </Text>
        </Stack>
        <StatusPill status={row.status} absent={row.absent} />
      </Group>
    </Group>
  );
}

export default OneOnOneBySessionView;
